import { accessToken, AppConfig, forget, signIn } from "./auth";

// Calls the app's API (functions/app-api): the connector's tools by name, so the app and Claude never disagree.

/**
 * Acts the platform treats as one request when the same person repeats them with the same arguments within a minute
 * (retry protection). A press in the app is always meant, so each gets its own idempotency key: approve, withdraw,
 * approve again in a minute really approves again. send_draft is left out on purpose: its key is the email's content,
 * so the same email can't go twice.
 */
export const KEYED_ACTS = new Set([
  "review_draft", "discard_draft", "approve_vendor_po", "withdraw_approval", "prepare_po_email", "settle_document",
  "work_exception", "decide_po_change", "record_agent_order", "record_agent_shipment", "approve_qb_entry", "record_credit",
]);

export function withPressKey(name: string, args: Record<string, unknown>, key: () => string): Record<string, unknown> {
  return KEYED_ACTS.has(name) && args.idempotency_key === undefined ? { ...args, idempotency_key: `app-${key()}` } : args;
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

export class Api {
  constructor(private readonly cfg: AppConfig) {}

  private async request<T>(method: "GET" | "POST", path: string, body?: unknown, retried = false): Promise<T> {
    const token = await accessToken(this.cfg);
    if (!token) return signIn(this.cfg);
    const r = await fetch(path, {
      method,
      headers: { Authorization: `Bearer ${token}`, ...(body === undefined ? {} : { "Content-Type": "application/json" }) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (r.status === 401 && !retried) {
      forget(); // the token was refused (revoked or expired early): sign in again
      return this.request<T>(method, path, body, true);
    }
    const out = (await r.json().catch(() => ({}))) as { error?: string; message?: string };
    if (!r.ok) throw new ApiError(r.status, out.message ?? out.error ?? `the platform answered ${r.status}`);
    return out as T;
  }

  me<T>(): Promise<T> {
    return this.request<T>("GET", "/api/me");
  }

  tool<T>(name: string, args: Record<string, unknown> = {}): Promise<T> {
    return this.request<T>("POST", `/api/tools/${name}`, withPressKey(name, args, () => crypto.randomUUID()));
  }
}
